import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineEditV2Directive } from '../../base-template-editor-v2/inline-edit/inline-edit-v2.directive';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import {
  PropertyDescriptionInitModel,
  PropertyDescriptionInitVariables,
  PropertyDescriptionModel,
  PropertyDescriptionVariables,
} from '../../../../models/email-template/property-description.interface';

/**
 * Mockup-styled Property Description block - a drop-in replacement for
 * app-property-description in block-renderer-v2 (same [block]/[isPreview]
 * inputs and outputs), so it covers both the center-canvas summary
 * (isPreview=true) and the right-panel edit form (isPreview=false). Reuses
 * the real, unmodified app-block-text (image upload/segment/validation),
 * app-field-editor-v2 and app-pin sub-components and the exact same
 * PropertyDescriptionVariables field names - only the surrounding layout is
 * new. Image toggle behavior mirrors Main Banner's own desktop/mobile
 * upload-vs-link switch (see main-banner-v2).
 */
@Component({
  selector: 'app-property-description-v2',
  imports: [CommonModule, ReactiveFormsModule, BlockTextComponent, FieldEditorV2Component, InlineEditV2Directive, InlineMediaV2Directive],
  templateUrl: './property-description-v2.component.html',
  styleUrl: './property-description-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PropertyDescriptionComponentV2 extends BaseComponent implements OnInit {
  @Input() block: PropertyDescriptionModel = new PropertyDescriptionInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;
  /** Center-canvas summary shows the desktop OR mobile image to match the
   *  canvas's own device toggle, not both stacked (see main-banner-v2). */
  @Input() deviceMode: 'desktop' | 'mobile' = 'desktop';

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new PropertyDescriptionInitVariables();
    }
  }

  imageDesktopPlaceholder = 'Desktop Image';
  imageMobilePlaceholder = 'Mobile Image';
  imageHrefPlaceholder = 'Enter Image Redirect Link';
  imageAltPlaceholder = 'Enter Image Alt Text';
  namePlaceholder = 'Enter Property Name';
  descriptionPlaceholder = 'Enter Description';
  ctaLinkPlaceholder = 'Enter CTA Link';
  ctaTextPlaceholder = 'Enter CTA Text';

  protected readonly ctaButtonPlaceholder = 'Insert CTA text';

  /** Drives app-block-text's own [isImage] input from outside (its internal
   *  toggle checkbox is hidden via CSS - see main-banner-v2's own scss). */
  protected desktopImageAsUpload = true;
  protected mobileImageAsUpload = true;

  protected toggleDesktopImageMode(): void {
    this.desktopImageAsUpload = !this.desktopImageAsUpload;
  }

  protected toggleMobileImageMode(): void {
    this.mobileImageAsUpload = !this.mobileImageAsUpload;
  }

  /** The image matching the canvas's current device toggle - undefined if
   *  that specific slot (desktop/mobile) hasn't been uploaded, even when the
   *  other one has (see main-banner-v2's currentDeviceImage). */
  get currentDeviceImage(): string | undefined {
    return this.block.variables?.[this.currentDeviceImageKey];
  }

  /** The variable the canvas image slot shows and replaces - an upload in
   *  mobile view replaces the mobile image, not the desktop one. */
  get currentDeviceImageKey(): 'PropertyDescImageMobile' | 'PropertyDescImageDesktop' {
    return this.deviceMode === 'mobile' ? 'PropertyDescImageMobile' : 'PropertyDescImageDesktop';
  }

  /** The redirect link opened by clicking the preview image - same treatment
   *  as Main Banner's previewHref: v1's exact helper.getFullUrl(...), plus a
   *  segment query param when a segment is selected. */
  get imagePreviewHref(): string {
    const base = this.helper.getFullUrl(this.block.variables?.['PropertyDescImageHref']);
    const segment = this.block.variables?.['PropertyDescImageHrefSegmentRegex'];
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  /** Same treatment for the CTA button's own link + segment. */
  get ctaPreviewHref(): string {
    const base = this.helper.getFullUrl(this.block.variables?.['PropertyDescCtaLink']);
    const segment = this.block.variables?.['PropertyDescCtaLinkSegmentRegex'];
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof PropertyDescriptionVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
